import type { RefCallback } from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';

import { fade, palette } from '../common/colors';
import { darkThemeSelector, styled } from '../stitches.config';

type HorizontalShadowEdges = 'start' | 'end' | 'both';
type VerticalShadowEdges = 'top' | 'bottom' | 'both';

export function useVerticalShadowOnScroll(visible: VerticalShadowEdges): [
  {
    [key in VerticalShadowEdges]: boolean;
  },
  RefCallback<HTMLElement>,
] {
  const [topEnabled, setTopEnabled] = useState(false);
  const [bottomEnabled, setBottomEnabled] = useState(false);
  const [bothEnabled, setBothEnabled] = useState(false);

  const scrollableRef: RefCallback<HTMLElement> = useCallback(
    (ref) => {
      const handleScroll = () => {
        if (!ref) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = ref;
        const isBottom = Math.ceil(clientHeight + scrollTop) >= scrollHeight;
        const isTop = scrollTop === 0;
        const isBetween = scrollTop > 0 && Math.ceil(clientHeight + scrollTop) < scrollHeight;

        setTopEnabled(!isTop && (visible === 'top' || visible === 'both'));
        setBottomEnabled(!isBottom && (visible === 'bottom' || visible === 'both'));
        setBothEnabled(isBetween && visible === 'both');
      };

      handleScroll();
      ref?.addEventListener('scroll', handleScroll);
      return () => {
        ref?.removeEventListener('scroll', handleScroll);
      };
    },
    [visible],
  );

  return [
    {
      top: topEnabled,
      bottom: bottomEnabled,
      both: bothEnabled,
    },
    scrollableRef,
  ];
}

const topShadowLight = `inset 0 11px 0 -10px ${fade(palette.gray800, 0.04)}, inset 0 12px 3px -10px ${fade(palette.gray800, 0.04)}`;
const topShadowDark = `inset 0 11px 0 -10px ${fade(palette.gray700, 0.8)}, inset 0 11px 1px -10px ${fade(palette.gray900, 0.8)}, inset 0 12px 3px -10px ${fade(palette.gray900, 0.8)}`;

const bottomShadowLight = `inset 0 -11px 0 -10px ${fade(palette.gray800, 0.04)}, inset 0 -12px 3px -10px ${fade(palette.gray800, 0.04)}`;
const bottomShadowDark = `inset 0 -11px 0 -10px ${fade(palette.gray700, 0.8)}, inset 0 -11px 1px -10px ${fade(palette.gray900, 0.8)}, inset 0 -12px 3px -10px ${fade(palette.gray900, 0.8)}`;

export const VerticalScrollShadow = styled('div', {
  maxHeight: '100%',
  overflow: 'auto',

  '&::before, &::after': {
    content: '',
    position: 'absolute',
    zIndex: 100,
    right: 0,
    left: 0,
    display: 'block',
    height: '$12',
    pointerEvents: 'none',
  },

  '&::before': {
    top: 0,
  },

  '&::after': {
    bottom: 0,
  },

  variants: {
    top: {
      true: {},
      false: {},
    },
    bottom: {
      true: {},
      false: {},
    },
    both: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      top: true,
      css: {
        '&::before': {
          boxShadow: topShadowLight,

          [darkThemeSelector]: {
            boxShadow: topShadowDark,
          },
        },
      },
    },
    {
      bottom: true,
      css: {
        '&::after': {
          boxShadow: bottomShadowLight,

          [darkThemeSelector]: {
            boxShadow: bottomShadowDark,
          },
        },
      },
    },
    {
      both: true,
      css: {
        '&::before': {
          boxShadow: topShadowLight,

          [darkThemeSelector]: {
            boxShadow: topShadowDark,
          },
        },

        '&::after': {
          boxShadow: bottomShadowLight,

          [darkThemeSelector]: {
            boxShadow: bottomShadowDark,
          },
        },
      },
    },
  ],
});
VerticalScrollShadow.displayName = 'VerticalScrollShadow';

export function useHorizontalShadowOnScroll(visible: HorizontalShadowEdges): [
  {
    [key in HorizontalShadowEdges]: boolean;
  },
  RefCallback<HTMLElement>,
] {
  const [startEnabled, setStartEnabled] = useState(false);
  const [endEnabled, setEndEnabled] = useState(false);
  const [bothEnabled, setBothEnabled] = useState(false);

  const scrollableRef: RefCallback<HTMLElement> = useCallback(
    (ref) => {
      const handleScroll = () => {
        if (!ref) {
          return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = ref;
        const isRight = Math.ceil(clientWidth + scrollLeft) >= scrollWidth;
        const isLeft = scrollLeft === 0;
        const isBetween = scrollLeft > 0 && Math.ceil(clientWidth + scrollLeft) < scrollWidth;

        setStartEnabled(!isLeft && (visible === 'start' || visible === 'both'));
        setEndEnabled(!isRight && (visible === 'end' || visible === 'both'));
        setBothEnabled(isBetween && visible === 'both');
      };

      handleScroll();
      ref?.addEventListener('scroll', handleScroll);
      return () => {
        ref?.removeEventListener('scroll', handleScroll);
      };
    },
    [visible],
  );

  return [
    {
      start: startEnabled,
      end: endEnabled,
      both: bothEnabled,
    },
    scrollableRef,
  ];
}

const startShadowLight = `inset 11px 0 0 -10px ${fade(palette.gray800, 0.04)}, inset 12px 0 3px -10px ${fade(palette.gray800, 0.04)}`;
const startShadowDark = `inset 11px 0 0 -10px ${fade(palette.gray700, 0.8)}, inset 11px 0 1px -10px ${fade(palette.gray900, 0.8)}, inset 12px 0 3px -10px ${fade(palette.gray900, 0.8)}`;

const endShadowLight = `inset -11px 0 0 -10px ${fade(palette.gray800, 0.04)}, inset -12px 0 3px -10px ${fade(palette.gray800, 0.04)}`;
const endShadowDark = `inset -11px 0 0 -10px ${fade(palette.gray700, 0.8)}, inset -11px 0 1px -10px ${fade(palette.gray900, 0.8)}, inset -12px 0 3px -10px ${fade(palette.gray900, 0.8)}`;

export const HorizontalScrollShadow = styled('div', {
  maxWidth: '100%',
  overflow: 'auto',

  '&::before, &::after': {
    content: '',
    position: 'absolute',
    zIndex: 100,
    top: 0,
    bottom: 0,
    display: 'block',
    width: '$12',
    pointerEvents: 'none',
  },

  '&::before': {
    left: 0,
  },

  '&::after': {
    right: 0,
  },

  variants: {
    start: {
      true: {},
      false: {},
    },
    end: {
      true: {},
      false: {},
    },
    both: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      start: true,
      css: {
        '&::before': {
          boxShadow: startShadowLight,

          [darkThemeSelector]: {
            boxShadow: startShadowDark,
          },
        },
      },
    },
    {
      end: true,
      css: {
        '&::after': {
          boxShadow: endShadowLight,

          [darkThemeSelector]: {
            boxShadow: endShadowDark,
          },
        },
      },
    },
    {
      both: true,
      css: {
        '&::before': {
          boxShadow: startShadowLight,

          [darkThemeSelector]: {
            boxShadow: startShadowDark,
          },
        },

        '&::after': {
          boxShadow: endShadowLight,

          [darkThemeSelector]: {
            boxShadow: endShadowDark,
          },
        },
      },
    },
  ],
});
HorizontalScrollShadow.displayName = 'HorizontalScrollShadow';

/**
 * Overflow-edge state for a scroll container owned elsewhere (an element, not
 * a ref callback). Unlike the scroll-only hooks above, this also tracks
 * content growth: scrollWidth moves without any scroll event or box resize
 * when overflowing content (table rows, columns) renders after mount.
 */
export function useHorizontalOverflowEdges(element: HTMLElement | null): {
  start: boolean;
  end: boolean;
} {
  const [edges, setEdges] = useState({ start: false, end: false });

  useLayoutEffect(() => {
    if (!element) {
      setEdges({ start: false, end: false });
      return undefined;
    }
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      const start = scrollLeft > 0;
      const end = Math.ceil(clientWidth + scrollLeft) < scrollWidth;
      setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    element.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true });
    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element]);

  return edges;
}

/** Vertical counterpart to {@link useHorizontalOverflowEdges}, as a ref callback. */
export function useVerticalOverflowEdges(): [
  { top: boolean; bottom: boolean },
  RefCallback<HTMLElement>,
] {
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const scrollableRef: RefCallback<HTMLElement> = useCallback((element) => {
    if (!element) {
      return undefined;
    }
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const top = scrollTop > 0;
      const bottom = Math.ceil(clientHeight + scrollTop) < scrollHeight;
      setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    };
    update();
    element.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return [edges, scrollableRef];
}

export function ShadowOnScrollOverflowContents({ children }: { children: React.ReactNode }) {
  const [horizontalScrollShadow, scrollRef] = useHorizontalShadowOnScroll('both');

  return (
    <HorizontalScrollShadow {...horizontalScrollShadow} ref={scrollRef}>
      {children}
    </HorizontalScrollShadow>
  );
}
